import React from 'react'
import { Link } from 'react-router-dom'

/**
 * Privacy Policy — public, anonymous-reachable (referenced by the SMS 10DLC
 * registration and linked from the Terms of Service). Rendered with the app's
 * design tokens (no raw hex).
 */

const sectionStyle: React.CSSProperties = { marginBottom: 28 }
const h2Style: React.CSSProperties = { fontSize: 18, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 10px', letterSpacing: '-0.01em' }
const h3Style: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '14px 0 4px' }
const pStyle: React.CSSProperties = { fontSize: 15, color: 'var(--ink-secondary)', lineHeight: 1.7, margin: 0 }
const ulStyle: React.CSSProperties = { fontSize: 15, color: 'var(--ink-secondary)', lineHeight: 1.7, margin: '8px 0 0', paddingLeft: 20 }
const liStyle: React.CSSProperties = { marginBottom: 4 }
const highlightStyle: React.CSSProperties = { padding: '16px 18px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 12, margin: '0 0 28px' }
const highlightLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent-text)', margin: '0 0 6px' }
const linkStyle: React.CSSProperties = { color: 'var(--accent-text)', textDecoration: 'none', fontWeight: 600 }

export default function Privacy() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', color: 'var(--ink-primary)', padding: '48px 24px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Link to="/" style={{ fontSize: 13, color: 'var(--ink-tertiary)', textDecoration: 'none' }}>← Back to home</Link>

        <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.03em', margin: '20px 0 8px' }}>Privacy Policy</h1>
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 24px' }}>Last updated: July 8, 2026</p>

        <p style={{ ...pStyle, marginBottom: 24 }}>
          Orkestra (“Orkestra,” “we,” “us,” “our”) is a field-service operations platform serving service
          businesses such as HVAC, plumbing, and cleaning companies. This policy covers two categories of
          data: <strong>Customers</strong> — the service businesses and their staff who use Orkestra — and
          <strong> End Customers</strong> — those businesses’ own customers.
        </p>

        <div style={highlightStyle}>
          <p style={highlightLabel}>SMS consent &amp; phone numbers</p>
          <p style={{ ...pStyle, color: 'var(--ink-primary)' }}>
            Orkestra does not sell, rent, or share End Customer phone numbers, mobile opt-in data, or SMS
            consent information with any third parties for their own marketing purposes. Phone numbers and
            consent records are used solely to enable the service business you are a customer of to
            communicate with you about your service, and are shared only with the service providers strictly
            necessary to deliver those messages (such as our SMS carrier). Consent to receive SMS is never a
            condition of purchasing any product or service.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>1. Information We Collect</h2>
          <h3 style={h3Style}>Customers (service businesses &amp; staff)</h3>
          <p style={pStyle}>Account and contact details, authentication information, billing information, and the content they create in the Service.</p>
          <h3 style={h3Style}>End Customers</h3>
          <p style={pStyle}>Contact information entered by a business (such as name, phone, email, and address); service records; communications, including email and SMS content; and SMS consent records — whether consent was given, when, the method, and the exact consent language shown.</p>
          <h3 style={h3Style}>Automatically collected</h3>
          <p style={pStyle}>Technical, usage, log, and error data collected as you use the Service.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>2. How We Use Information</h2>
          <ul style={ulStyle}>
            <li style={liStyle}>To provide, operate, and secure the Service.</li>
            <li style={liStyle}>To enable communication between a business and its End Customers via email and SMS about service, appointments, jobs, invoices, and support.</li>
            <li style={liStyle}>To process billing and send transactional messages.</li>
            <li style={liStyle}>To detect fraud and abuse.</li>
            <li style={liStyle}>To comply with legal obligations.</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>3. SMS / Text Messaging</h2>
          <h3 style={h3Style}>Consent</h3>
          <p style={pStyle}>End Customers opt in to SMS either verbally when contacting the business, or via an explicit opt-in checkbox during customer-portal onboarding. Consent is affirmative and is never a condition of service.</p>
          <h3 style={h3Style}>Message types</h3>
          <p style={pStyle}>Messages are transactional and customer-service in nature — for example, appointment confirmations and reminders, technician updates, and responses to your inquiries.</p>
          <h3 style={h3Style}>Opt-out</h3>
          <p style={pStyle}>Reply STOP at any time to opt out, or HELP for help. Message and data rates may apply, and message frequency varies. We do not use these numbers for any third-party marketing.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>4. How We Share Information</h2>
          <p style={pStyle}>We do not sell personal information. We share information only:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>With subprocessors that help us run the Service: Supabase (database and authentication), Stripe (payments), Resend (email), Twilio (SMS), Vercel (hosting), and Sentry (error monitoring).</li>
            <li style={liStyle}>Between a service business and its own End Customers.</li>
            <li style={liStyle}>For legal reasons, where required.</li>
            <li style={liStyle}>In connection with a business transfer.</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>5. Data Retention</h2>
          <p style={pStyle}>We retain information as needed to provide the Service, meet legal obligations, and resolve disputes. Service businesses control the End Customer records held in their accounts.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>6. Data Security</h2>
          <p style={pStyle}>We use access controls that isolate each business’s data, encrypted connections, and role-based permissions. No method of transmission or storage is fully secure.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>7. Your Choices and Rights</h2>
          <ul style={ulStyle}>
            <li style={liStyle}>SMS: reply STOP to opt out at any time.</li>
            <li style={liStyle}>Access and deletion: End Customers may contact the business they work with, or us, to request access or deletion.</li>
            <li style={liStyle}>Account information can be edited within the Service.</li>
            <li style={liStyle}>You may have additional rights depending on your location.</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>8. Children’s Privacy</h2>
          <p style={pStyle}>The Service is not directed to children under 13, and we do not knowingly collect their information.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>9. Changes to This Policy</h2>
          <p style={pStyle}>We may update this policy from time to time. When we do, we will revise the “Last updated” date above and communicate material changes.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>10. Contact</h2>
          <p style={pStyle}>
            Orkestra — questions about this Privacy Policy can be sent to{' '}
            <a href="mailto:support@orkestrapro.com" style={linkStyle}>support@orkestrapro.com</a>.
          </p>
        </div>
      </div>
    </div>
  )
}
